/* eslint-disable max-lines */
import {ArrUtil, logger, ObjUtil, Util} from 'yougile-util'
import {Connections, ConnectionsJSON} from './connections'
import {
  movementTemplatesContainerId,
  noBoardContainerId,
  repeatTemplatesContainerId,
  rootNodeId,
  taskTemplatesContainerId
} from './constants'
import {
  DataObject,
  DataObjectId,
  DataObjectJSON,
  DataObjectTypeName
} from './data-object'
import {Entity} from './entity'
import {HubUtil} from './hub-util'
import {Hub, HubJSON, isHub} from './hubs'
import {ObjectType} from './object-type'
import {Sandbox, SandboxJSON} from './sandbox'
import {Tag, TagJSON} from './tag'
import {isRoleEmail, User, UserJSON, UserPrivileges} from './user'

export type Path = string & {__type: 'Path'}

export interface SandboxTreeOpts {
  withDeleted?: boolean
  withHidden?: boolean
}

/**
 * Organizes Entities in tree structure and adds associates
 * a single unique path to each Entity.
 * All paths start with /root.
 */
export class SandboxTree extends Sandbox {
  private static duplicateTitlesReported: Set<string> = new Set()
  private static readonly MAX_REPORTED_ERRORS = 500
  private treeIndex: Map<DataObjectId, Set<DataObjectId>>
  private levelCache: Map<DataObjectId, number>
  private shortIdIndex: Map<string, DataObjectId>

  public static fromJsonData(
    json: SandboxJSON | undefined,
    tryReuseObject: (
      companyId: DataObjectId,
      id: DataObjectId
    ) => DataObject | undefined,
    fromSandbox?: SandboxTree
  ): SandboxTree | undefined {
    if (!json || (fromSandbox && json.companyId !== fromSandbox.companyId)) {
      return undefined
    }

    const [allEntities, allConnections] = SandboxTree.objectsFromIndex(
      json.companyId,
      json.index,
      tryReuseObject
    )

    let res: SandboxTree
    if (fromSandbox) {
      res = fromSandbox
      res.setAllValues(json.revision, new Map(), allEntities, allConnections)
    } else {
      res = new SandboxTree(
        json.companyId,
        json.revision,
        new Map(),
        allEntities,
        allConnections
      )
    }
    res.updateUsers(json.users)
    return res
  }

  public static encodePath(path: string): string {
    // encode&decode should keep __* replacements for backwards compatibility
    path = path
      .replace(/-/g, '__dash__')
      .replace(/ /g, '-')
      .replace(/\//g, '__slash__')
    return encodeURIComponent(path)
  }

  public static decodePath(path: string): string {
    // decodeURIComponent happens above, don't place it here b/c it breaks '%' decoding
    return path
      .replace(/-/g, ' ')
      .replace(/__dash__/g, '-')
      .replace(/__slash__/g, '/')
  }

  private static objectsFromIndex(
    companyId: DataObjectId,
    index: {[id: string]: DataObjectJSON},
    tryReuseObject: (
      companyId: DataObjectId,
      id: DataObjectId
    ) => DataObject | undefined
  ): [{[id: string]: Entity}, Connections[]] {
    const ent: {[id: string]: Entity} = {}
    const conn: Connections[] = []

    for (const id in index) {
      if (!Object.prototype.hasOwnProperty.call(index, id)) {
        continue
      }
      const obj = index[id]
      if (!obj || (!obj.id && id.length !== 36)) {
        continue
      }
      if (!obj.id) {
        logger.warn('No id in object', obj)
      }
      const objWithRealId = obj as DataObjectJSON & {realId?: DataObjectId}
      if (!obj.id && objWithRealId.realId) {
        obj.id = objWithRealId.realId
      }
      if (!obj.id && id) {
        obj.id = id as DataObjectId
      }
      if (obj.dataType === 'Hub') {
        const newHub =
          (tryReuseObject(companyId, obj.id) as Hub) ?? new Hub(companyId, '')
        ent[obj.id] = newHub.fromJSON(companyId, obj as HubJSON)
      } else if (obj.dataType === 'Tag') {
        const newTag =
          (tryReuseObject(companyId, obj.id) as Tag) ?? new Tag(companyId, '')
        ent[obj.id] = newTag.fromJSON(companyId, obj as TagJSON)
      } else if (obj.dataType === 'User') {
        const newUser =
          (tryReuseObject(companyId, obj.id) as User) ??
          new User(companyId, obj.id, '')
        ent[obj.id] = newUser.fromJSON(companyId, obj as UserJSON)
      } else if (obj.dataType === 'Connections') {
        const newConn =
          (tryReuseObject(companyId, obj.id) as Connections) ||
          new Connections(companyId, (obj as ConnectionsJSON).from)
        conn.push(newConn.fromJSON(companyId, obj as ConnectionsJSON))
      }
    }
    return [ent, conn]
  }

  private addToShortIndex(id: DataObjectId): void {
    this.shortIdIndex.set(Util.shortId(id), id)
  }

  private removeFromShortIndex(id: DataObjectId): void {
    this.shortIdIndex.delete(Util.shortId(id))
  }

  constructor(
    companyId: DataObjectId,
    revision: number,
    users: Map<DataObjectId, UserPrivileges>,
    entities: {[id: string]: Entity},
    allConnections: Connections[]
  ) {
    super(companyId, revision, users, entities, allConnections)
    this.treeIndex = new Map()
    this.levelCache = new Map()
    this.shortIdIndex = new Map()
    this.rebuildTreeIndex()
  }

  public rebuildTreeIndex(): void {
    this.treeIndex.clear()
    this.shortIdIndex.clear()
    this.addToShortIndex(rootNodeId)
    ObjUtil.forEach(this.entities, (_id, e) => this.addNodeToTreeIndex(e))
  }

  /**
   * When you want to show some hub on client, but don‘t want it to be
   * sent to server
   */
  public addPhantom(hub: Hub, hostId: DataObjectId): void {
    hub.companyId = this.companyId
    hub.location = hostId
    hub.properties.phantom = true
    this.entities[hub.id] = hub
    this.treeIndex.set(hub.id, new Set())
    this.treeIndex.get(hub.location)?.add(hub.id)
    this.addToShortIndex(hub.id)
  }

  public removePhantom(hub: Hub): void {
    if (!HubUtil.isPhantom(hub)) {
      logger.error('Tried to removePhantom on normal hub')
      return
    }
    delete hub.properties.phantom
    delete this.entities[hub.id]
    this.treeIndex.delete(hub.id)
    this.treeIndex.get(hub.location)?.delete(hub.id)
    this.removeFromShortIndex(hub.id)
  }

  /**
   * Overriding Entity addition.
   */
  public override add(
    ent: Entity,
    hostId: DataObjectId,
    resolveNameConflicts = false,
    noAutoCommit = false
  ): void {
    super.add(ent, hostId)
    this.registerTreeNode(ent.location, ent, resolveNameConflicts, noAutoCommit)
  }

  public startPath(): Path {
    return ('/' + Util.shortId(this.companyId)) as Path
  }

  /**
   * This path can uniquely identify hub on server.
   * It is used to create urls.
   * Example: /551c1d62669d/mega-project/#some-tag
   * Where 551c1d62669d – is a last part of the corresponding companyId UUID.
   */
  public calculatePath(hub: Hub): Path {
    if (!hub) {
      throw new Error('No hub specified')
    }
    const esc = SandboxTree.encodePath

    if (hub.companyId === hub.id) {
      return this.startPath()
    }
    const host = this.getHub(hub.location)
    if (!host) {
      throw new Error('No host found')
    }
    return (this.calculatePath(host) + '/' + esc(hub.title)) as Path
  }

  /**
   * Returns id of Hub from the path.
   */
  public findIdByPath(
    path: Path,
    allowUndefined = false
  ): DataObjectId | undefined {
    const parts = path.split('/')
    parts.shift() // remove empty part
    const companyPart = '/' + parts.shift()
    if (companyPart !== this.startPath()) {
      throw new Error('Incorrect path')
    }
    const unesc = SandboxTree.decodePath

    let currentId: DataObjectId | undefined = this.companyId
    for (const part of parts) {
      currentId = this.findIdByTitle(
        'Hub',
        unesc(part),
        currentId,
        allowUndefined
      )
      if (!currentId && allowUndefined) {
        return undefined
      }
      if (!currentId) {
        throw new Error('Not found by path')
      }
    }
    return currentId
  }

  public getChildrenIds(parentId: DataObjectId): Set<DataObjectId> {
    const ix = this.treeIndex.get(parentId)
    if (!parentId || !ix) {
      throw new Error('No host found')
    }
    return ix
  }

  public idsToHubs(ids: Set<DataObjectId>, opts: SandboxTreeOpts = {}): Hub[] {
    const {withDeleted = false, withHidden = false} = opts
    const res: Hub[] = []
    ids.forEach((objId: DataObjectId) => {
      const e = this.get(objId)
      if (
        e &&
        e instanceof Hub &&
        (withDeleted || !e.deleted) &&
        (withHidden || !e.title.startsWith('.'))
      ) {
        res.push(e)
      }
    })
    return res
  }

  public getChildrenEntities(
    parentId: DataObjectId,
    opts: SandboxTreeOpts = {}
  ): Entity[] {
    const {withDeleted = false, withHidden = false} = opts
    const res: Entity[] = []
    this.getChildrenIds(parentId).forEach((objId: DataObjectId) => {
      const e = this.get(objId)
      if (
        e &&
        e instanceof Entity &&
        (withDeleted || !e.deleted) &&
        (withHidden || !e.title.startsWith('.'))
      ) {
        res.push(e)
      }
    })
    return res
  }

  public getChildrenHubs(
    parentId: DataObjectId,
    opts: SandboxTreeOpts = {}
  ): Hub[] {
    return this.idsToHubs(this.getChildrenIds(parentId), opts)
  }

  public hasChildWithTitle(
    hostId: DataObjectId,
    title: string,
    dataType: DataObjectTypeName
  ): boolean {
    const ix = this.treeIndex.get(hostId)
    if (!hostId || !ix) {
      throw new Error('No host found ' + hostId)
    }
    if (!title || typeof title !== 'string') {
      throw new Error('Not valid name')
    }
    for (const ch of ix) {
      const child = this.get(ch)
      if (child && child.title === title && child.dataType === dataType) {
        return true
      }
    }
    return false
  }

  public addNodeToTreeIndex(node: {
    id: DataObjectId
    location: DataObjectId
  }): void {
    if (!this.treeIndex.has(node.id)) {
      this.treeIndex.set(node.id, new Set())
    }
    const ix = this.treeIndex.get(node.location)
    if (!ix) {
      this.treeIndex.set(node.location, new Set([node.id]))
    } else {
      ix.add(node.id)
    }
    this.addToShortIndex(node.id)
  }

  public registerTreeNode(
    location: DataObjectId,
    node: Entity,
    resolveNameConflicts: boolean,
    noAutoCommit: boolean
  ): void {
    if (!this.treeIndex.has(node.id)) {
      this.treeIndex.set(node.id, new Set())
    }
    if (!this.treeIndex.has(location)) {
      this.treeIndex.set(location, new Set())
    }
    const locIx = this.treeIndex.get(location) ?? new Set()
    if (locIx.has(node.id)) {
      logger.warn('Node already added')
      return
    }
    if (resolveNameConflicts && location !== noBoardContainerId) {
      if (this.hasChildWithTitle(location, node.title, node.dataType)) {
        if (node instanceof Entity) {
          node.title = this.notConflictingTitle(
            node.title,
            node.dataType,
            location
          )
          node.commit()
          logger.warn(
            'Name conflict detected: ' + node.id + ', fixed => ' + node.title
          )
        } else {
          logger.error('Unrecognized conflict, cannot fix')
          return
        }
      }
    }
    if (!this.has(node.id)) {
      throw new Error('Cannot add unregistered node to the tree')
    }
    locIx.add(node.id)
    this.addToShortIndex(node.id)
    node.location = location
    if (!noAutoCommit) {
      node.commit()
    }
  }

  public notConflictingTitle(
    title: string,
    dataType: DataObjectTypeName,
    hostId: DataObjectId
  ): string {
    if (hostId === noBoardContainerId || hostId === taskTemplatesContainerId) {
      return title
    }
    if (!this.hasChildWithTitle(hostId, title, dataType)) {
      return title
    } else {
      if (/ \(\d+\)$/.test(title)) {
        title = title.split(' (').slice(0, -1).join(' (')
      }
      let n = 2
      while (this.hasChildWithTitle(hostId, title + ' (' + n + ')', dataType)) {
        n++
        if (n > 100) {
          return title + ' (' + Util.longId() + ')'
        }
      }
      return title + ' (' + n + ')'
    }
  }

  public moveTreeNode(
    node: Entity,
    oldLocation: DataObjectId,
    newLocation: DataObjectId,
    resolveNameConflicts: boolean,
    noAutoCommit: boolean
  ): void {
    if (oldLocation === newLocation) {
      logger.warn('Moving into the same location', oldLocation)
      return
    }
    this.registerTreeNode(newLocation, node, resolveNameConflicts, noAutoCommit)
    this.treeIndex.get(oldLocation)?.delete(node.id)
  }

  public findHubByTitle(
    title: string,
    hostId: DataObjectId,
    allowUndefined = false
  ): Hub | undefined {
    const id = this.findIdByTitle('Hub', title, hostId, allowUndefined)
    if (!id) {
      return undefined
    }
    return this.getHub(id)
  }

  public findTagByTitle(
    title: string,
    hostId: DataObjectId,
    allowUndefined = false
  ): Tag | undefined {
    const id = this.findIdByTitle('Tag', title, hostId, allowUndefined)
    if (!id) {
      return undefined
    }
    return this.getTag(id)
  }

  public findIdByTitle(
    dataType: 'User' | 'Hub' | 'Tag',
    title: string,
    hostId: DataObjectId,
    allowUndefined = false
  ): DataObjectId | undefined {
    const ix = this.treeIndex.get(hostId)
    if (!ix) {
      throw new Error('No such host registered')
    }
    const found: Entity[] = []
    let foundId: DataObjectId | undefined
    for (const childId of ix) {
      const child = this.get(childId)
      if (child && child.title === title && child.dataType === dataType) {
        found.push(child)
        foundId ??= childId
      }
    }
    if (found.length === 1) {
      return foundId
    }
    if (found.length > 1) {
      const min = ArrUtil.min(found, item => item.timestamp)
      if (min?.id === foundId) {
        return foundId
      }
      const key = `${hostId}:${title}`
      if (SandboxTree.duplicateTitlesReported.has(key)) {
        return foundId
      }
      if (
        SandboxTree.duplicateTitlesReported.size >=
        SandboxTree.MAX_REPORTED_ERRORS
      ) {
        return foundId
      }
      SandboxTree.duplicateTitlesReported.add(key)
      logger.error(
        'SandboxTree found by name not oldest',
        title,
        this.companyId,
        hostId,
        found.length,
        foundId,
        min?.id
      )
      return foundId
    }
    if (allowUndefined) {
      return undefined
    } else {
      throw new Error('Not found: ' + title)
    }
  }

  public findByShortId(shortId: string): Entity | undefined {
    if (!Util.isShortId(shortId)) {
      return undefined
    }
    const id = this.shortIdIndex.get(shortId)
    if (!id) {
      return undefined
    }
    return this.get(id)
  }

  public getType(entity: Entity): ObjectType {
    if (entity.location === noBoardContainerId) {
      return 'task'
    }
    if (entity.location === repeatTemplatesContainerId) {
      return 'other' // 'repeat-template'
    }
    if (entity.location === taskTemplatesContainerId) {
      return 'other' // 'task-template'
    }
    if (entity.location === movementTemplatesContainerId) {
      return 'other' // 'movement-template'
    }
    const level = this.getLevel(entity)
    switch (level) {
      case 4:
        return 'task'
      case 3:
        return 'column'
      case 2:
        return 'board'
      case 1:
        return 'project'
      case 0:
        return 'company'
      default:
        return 'task'
    }
  }

  public getLevel(entity: Entity | undefined): number {
    if (!entity || entity.location === noBoardContainerId) {
      return 4 // task
    }
    const cached = this.levelCache.get(entity.id)
    if (cached) {
      return cached
    }
    const parentLevel = this.levelCache.get(entity.location)
    if (parentLevel) {
      if (parentLevel + 1 <= 3) {
        this.levelCache.set(entity.id, parentLevel + 1)
      }
      return parentLevel + 1
    }
    if (entity.id === this.companyId) {
      this.levelCache.set(entity.id, 0)
      return 0
    }
    const parent = this.get(entity.location)
    const res = Math.min(4, this.getLevel(parent) + 1)
    if (res <= 4) {
      this.levelCache.set(entity.id, res)
    }
    return res
  }

  /**
   * Walks hub tree recursively starting at startId.
   */
  public walkForward(
    startHub: Hub | undefined,
    cb: (hub: Hub, level: number) => boolean
  ): void {
    if (!startHub) {
      return
    }
    const walk = (curr: Hub, level: number): void => {
      const proceed = cb(curr, level)
      if (typeof proceed === 'boolean' && !proceed) {
        return
      }
      const children = this.getChildrenIds(curr.id)
      for (const objId of children) {
        const obj = this.getHub(objId)
        if (obj) {
          walk(obj, level + 1)
        }
      }
    }
    walk(startHub, 0)
  }

  public getAllParentHubs(entity: Entity): Hub[] {
    if (!entity) {
      return []
    }
    const result: Hub[] = []
    let currId = entity.location
    while (currId && currId !== rootNodeId) {
      const curr = this.getHub(currId)
      if (!curr) {
        break
      }
      result.push(curr)
      currId = curr.location
    }
    return result
  }

  public editTitle(
    ent: Hub | Tag | undefined,
    newTitle: string,
    cb?: () => void
  ): void {
    if (!ent || !newTitle || newTitle === '.') {
      return
    }
    if (!ent.title.startsWith('.') && newTitle.startsWith('.')) {
      newTitle = newTitle.slice(1)
      if (newTitle) {
        this.editTitle(ent, newTitle, cb)
      }
      return
    }
    if (ent.companyId !== this.companyId) {
      throw new Error('companyId not match!')
    }
    if (ent.location === repeatTemplatesContainerId && ent instanceof Hub) {
      if (!ent.data?.repeatTemplate) {
        return
      }
      ent.data.repeatTemplate.title = newTitle

      if (!HubUtil.isPhantom(ent)) {
        ent.commit()
      } else {
        cb?.()
      }

      return
    }
    if (newTitle === ent.title) {
      return
    }
    if (newTitle.toLocaleLowerCase() === ent.title.toLowerCase()) {
      ent.title = newTitle

      if (!HubUtil.isPhantom(ent)) {
        ent.commit()
      } else {
        cb?.()
      }
      return
    }
    let needResolveConflicts = true
    if (isHub(ent) && ent?.data?.mirrors?.length) {
      needResolveConflicts = false
    }

    if (needResolveConflicts) {
      newTitle = this.notConflictingTitle(newTitle, ent.dataType, ent.location)
    }
    if (
      needResolveConflicts &&
      ent.location !== noBoardContainerId &&
      this.hasChildWithTitle(ent.location, newTitle, ent.dataType)
    ) {
      throw new Error('This title already exists')
    }
    if (!newTitle || typeof newTitle !== 'string') {
      throw new Error('Incorrect new title')
    }
    ent.title = newTitle

    if (!HubUtil.isPhantom(ent)) {
      ent.commit()
    } else {
      cb?.()
    }
  }

  public remove(entity: Hub | Tag): void {
    if (!entity.deleted) {
      entity.deleted = true
      this.editTitle(entity, entity.title + '___deleted')
    }
  }

  public restore(entity: Hub | Tag): void {
    if (entity.deleted) {
      entity.deleted = false
      if (entity.title.includes('___deleted')) {
        this.editTitle(entity, entity.title.split('___deleted').shift() ?? '')
      } else {
        entity.commit()
      }
    }
  }

  public deleteUserFromProfile(userId: DataObjectId): void {
    if (this.get(this.companyId)?.title !== 'Profile') {
      throw new Error('Use UserAccess for non-profile sandboxes')
    }
    this.disconnect(userId, this.companyId)
  }

  public restoreUserInProfile(userId: DataObjectId): void {
    if (this.get(this.companyId)?.title !== 'Profile') {
      throw new Error('Use UserAccess for non-profile sandboxes')
    }
    this.connect(userId, this.companyId)
  }

  public isUserInCompany(userId: DataObjectId): boolean {
    return !!this.get(userId) && this.isConnected(userId, this.companyId)
  }

  public getAllUsers(
    withDeleted: boolean,
    includeIds?: string[],
    withRoleUsers = false
  ): User[] {
    return this.getChildrenEntities(rootNodeId).filter(
      (e: Entity) =>
        e instanceof User &&
        e.email !== undefined &&
        (withDeleted ||
          this.isUserInCompany(e.id) ||
          includeIds?.includes(e.id)) &&
        (withRoleUsers || !isRoleEmail(e.email))
    ) as User[]
  }

  public getProjectFor(
    objOrId: DataObject | DataObjectId | undefined
  ): Hub | undefined {
    let currentId: DataObjectId | undefined =
      objOrId instanceof DataObject ? objOrId.id : objOrId

    while (currentId && currentId !== rootNodeId) {
      const curr = this.get(currentId)
      if (!curr) {
        break
      }
      if (curr.location === this.companyId) {
        return curr as Hub
      }
      currentId = curr.location
    }
    return undefined
  }

  /**
   * Utility method for converting plain js objects into DataObjects.
   */
  public static jsonToObject(
    companyId: DataObjectId,
    json: DataObjectJSON | undefined
  ): DataObject | undefined {
    if (typeof json === 'undefined') {
      return undefined
    }
    if (!json?.dataType) {
      return undefined
    }
    switch (json.dataType) {
      case 'Hub':
        return new Hub(companyId, '', true).fromJSON(companyId, json as HubJSON)
      case 'Tag':
        return new Tag(companyId, '').fromJSON(companyId, json as TagJSON)
      case 'User':
        return new User(companyId, json.id, '').fromJSON(
          companyId,
          json as UserJSON
        )
      case 'Connections':
        return new Connections(
          companyId,
          (json as ConnectionsJSON).from
        ).fromJSON(companyId, json as ConnectionsJSON)
      default:
        throw new Error('Incorrect data type')
    }
  }
}
